import { useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import type { UseFormReturn } from 'react-hook-form';
import { Text } from '../../ui-components';
import { maxLength, regex } from '../../utils/validation';
import type { FieldConfig } from '../field';

const messages = defineMessages({
    required: {
        defaultMessage: 'Please enter a {field}',
        id: 'submit-answer.error.field.required',
    },
    requiredEmail: {
        defaultMessage: 'Please enter a valid email address',
        id: 'error.field.email',
    },
    maxLength: {
        defaultMessage: 'No more than {max} characters',
        id: 'error.field.maxLength',
    },
    noSpecialChars: {
        defaultMessage: 'No special characters',
        id: 'error.field.no.special.characters',
    },
    firstName: { defaultMessage: 'First Name', id: 'turn-to.labels.first-name' },
    lastName: { defaultMessage: 'Last Name', id: 'turn-to.labels.last-name' },
    email: { defaultMessage: 'Email', id: 'turn-to.labels.email' },
    nickName: { defaultMessage: 'Nickname', id: 'turn-to.labels.nickname' },
    invalidEmail: {
        id: 'footer.email.invalid_email_error',
        defaultMessage: 'Enter a valid email address',
    },
});

export function useSubmitAnswerFields(
    form: UseFormReturn<{
        answerText: string;
        firstName: string;
        lastName: string;
        email: string;
        nickName: string;
    }>,
): Record<string, FieldConfig> {
    const {
        control,
        formState: { errors },
    } = form;
    const intl = useIntl();

    const fields: Record<string, FieldConfig & { getValue?: () => string }> = {
        globalError: {
            labelRef: useRef<HTMLDivElement>(),
            name: 'globalError',
            label: 'globalError',
            type: 'hidden',
            control,
        },
        answerText: {
            id: 'turn-to-answer-text',
            name: 'answerText',
            helpText: (
                <Box display="flex" alignItems="center" gap={2} marginTop={2}>
                    <Text>
                        <FormattedMessage
                            id="turn-to.answer-helping-text"
                            defaultMessage="Do not include HTML, links, references to other stores, pricing, or contact info."
                        />
                    </Text>
                </Box>
            ),
            type: 'textarea',
            rules: {
                required: intl.formatMessage({
                    id: 'turn-to.enter-answer',
                    defaultMessage: 'Enter your answer',
                }),
            },
            error: errors.answerText,
            autoFocus: true,
            control,
        },
        firstName: {
            labelRef: useRef(),
            id: 'turn-to-answer-first-name-field',
            name: 'firstName',
            autoComplete: 'given-name',
            label: intl.formatMessage(messages.firstName),
            type: 'text',
            rules: {
                maxLength: {
                    value: maxLength.firstName,
                    message: intl.formatMessage(messages.maxLength, {
                        max: maxLength.firstName,
                    }),
                },
                pattern: {
                    value: regex.firstName,
                    message: intl.formatMessage(messages.noSpecialChars),
                },
                required: intl.formatMessage(messages.required, { field: 'First name' }),
            },
            error: errors.firstName,
            control,
        },
        lastName: {
            labelRef: useRef(),
            id: 'turn-to-answer-first-name-field',
            name: 'lastName',
            autoComplete: 'family-name',
            label: intl.formatMessage(messages.lastName),
            type: 'text',
            rules: {
                maxLength: {
                    value: maxLength.lastName,
                    message: intl.formatMessage(messages.maxLength, {
                        max: maxLength.lastName,
                    }),
                },
                pattern: {
                    value: regex.lastName,
                    message: intl.formatMessage(messages.noSpecialChars),
                },
                required: intl.formatMessage(messages.required, { field: 'Last name' }),
            },
            error: errors.lastName,
            control,
        },
        email: {
            labelRef: useRef(),
            id: 'turn-to-answer-first-name-field',
            name: 'email',
            autoComplete: 'email',
            label: intl.formatMessage(messages.email),
            type: 'email',
            rules: {
                maxLength: {
                    value: maxLength.email,
                    message: intl.formatMessage(messages.maxLength, {
                        max: maxLength.email,
                    }),
                },
                pattern: {
                    value: regex.email,
                    message: intl.formatMessage(messages.invalidEmail),
                },
                required: intl.formatMessage(messages.requiredEmail),
            },
            error: errors.email,
            control,
        },
        nickName: {
            labelRef: useRef(),
            id: 'turn-to-answer-first-name-field',
            name: 'nick-name',
            label: intl.formatMessage(messages.nickName),
            type: 'text',
            helpText: (
                <Text>
                    <FormattedMessage
                        id="turn-to.submit-modal.nickname-helper"
                        defaultMessage="Leave blank to use your first name and last initial"
                    />
                </Text>
            ),
            rules: {
                maxLength: {
                    value: maxLength.firstName,
                    message: intl.formatMessage(messages.maxLength, {
                        max: maxLength.firstName,
                    }),
                },
                pattern: {
                    value: regex.firstName,
                    message: intl.formatMessage(messages.noSpecialChars),
                },
            },
            error: errors.nickName,
            control,
        },
    };

    return fields;
}
